// src/components/MiCuentaPaciente.tsx
//
// Dos secciones del panel del paciente: con quién se atendió, y sus recetas.
//
// SE MUESTRA LO QUE HAY
// ---------------------
// Ninguna sección se dibuja vacía. Alguien que va por uñas nunca va a tener
// recetas, así que esa sección simplemente no existe para ella: no hace falta
// mantener listas de qué mostrarle a cada rubro, alcanza con preguntar si hay
// datos.
//
// EL DOCUMENTO SE PIDE A PEDIDO, NO AL REGISTRARSE
// ------------------------------------------------
// Las recetas se emiten a nombre de un DNI: el profesional lo tipea en su
// panel, y no queda ni el mail ni la cuenta del paciente. Así que sin
// documento cargado no hay forma honesta de saber cuáles son suyas.
//
// Pero el documento se pide SOLO si la persona quiere ver sus recetas, con un
// botón y un modal. Quien no las necesita nunca tiene que darnos su DNI, que
// además es lo correcto: no se junta un dato sensible "por las dudas".

import React, { useEffect, useState, useCallback } from 'react';
import { FileText, Stethoscope, ExternalLink, IdCard, X, Loader2, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface Profesional {
  id: number;
  nombre: string;
  apellido: string;
  especialidad: string | null;
  slug: string | null;
  turnos: number;
  ultima_visita: string;
}

interface Receta {
  id: number;
  cuir: string;
  issued_at: string;
  status: string;
  diagnosis_description: string | null;
  qbi2_s3_link: string | null;
  profesional_nombre: string;
  profesional_apellido: string;
  especialidad: string | null;
}

interface Props {
  patientId: number;
}

const fecha = (v: string) => {
  if (!v) return '';
  const d = new Date(v.includes('T') ? v : `${v}T12:00:00`);
  return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const MiCuentaPaciente: React.FC<Props> = ({ patientId }) => {
  const [profesionales, setProfesionales] = useState<Profesional[]>([]);
  const [recetas, setRecetas] = useState<Receta[]>([]);
  const [tieneDni, setTieneDni] = useState(true);
  const [cargando, setCargando] = useState(true);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [dni, setDni] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/patients/${patientId}/mi-cuenta`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const j = await r.json();
      if (r.ok && j.success) {
        setProfesionales(j.profesionales || []);
        setRecetas(j.recetas || []);
        setTieneDni(Boolean(j.tieneDni));
      }
    } catch {
      /* si falla, las secciones no se muestran y el panel sigue andando */
    } finally {
      setCargando(false);
    }
  }, [patientId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const guardarDni = async () => {
    const limpio = dni.replace(/\D/g, '');
    if (limpio.length < 6 || limpio.length > 9) {
      setError('Revisá el número: tiene que tener entre 6 y 9 dígitos.');
      return;
    }
    setGuardando(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/patients/${patientId}/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ dni: limpio }),
      });
      const j = await r.json();
      if (!r.ok || !j.success) {
        setError(j.error || 'No pudimos guardar tu documento.');
        return;
      }
      setModalAbierto(false);
      setCargando(true);
      await cargar();
    } catch {
      setError('No pudimos conectarnos. Probá de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) return null;

  const hayAlgoQueMostrar = profesionales.length > 0 || recetas.length > 0 || !tieneDni;
  if (!hayAlgoQueMostrar) return null;

  return (
    <div className="space-y-6">
      {/* ---------------------------------------------------- profesionales */}
      {profesionales.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-blue-600" />
            Tus profesionales
          </h3>
          <div className="space-y-3">
            {profesionales.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 last:border-0">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {p.nombre} {p.apellido}
                  </p>
                  <p className="text-xs text-gray-500">
                    {p.especialidad ? `${p.especialidad} · ` : ''}
                    {p.turnos} {p.turnos === 1 ? 'visita' : 'visitas'} · última el {fecha(p.ultima_visita)}
                  </p>
                </div>
                <a
                  href={`/p/${p.slug || p.id}`}
                  className="flex-shrink-0 inline-flex items-center gap-1.5 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                  <Calendar className="w-4 h-4" />
                  Pedir turno
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---------------------------------------------------------- recetas */}
      {(recetas.length > 0 || !tieneDni) && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-teal-600" />
            Tus recetas
          </h3>

          {!tieneDni ? (
            // No es lo mismo "no tenés recetas" que "no sabemos cuáles son
            // tuyas". Decirlo mal haría que alguien con recetas emitidas crea
            // que se perdieron.
            <div>
              <p className="text-sm text-gray-600 leading-relaxed">
                Las recetas se emiten a nombre de tu documento, así que necesitamos
                tu DNI para saber cuáles son tuyas. Se carga una sola vez.
              </p>
              <button
                onClick={() => setModalAbierto(true)}
                className="mt-3 inline-flex items-center gap-2 bg-teal-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors"
              >
                <IdCard className="w-4 h-4" />
                Cargar mi DNI
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {recetas.map((r) => (
                <div key={r.id} className="py-2 border-b border-gray-100 last:border-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 text-sm">
                        {r.diagnosis_description || 'Receta electrónica'}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {r.profesional_nombre} {r.profesional_apellido} · {fecha(r.issued_at)}
                      </p>
                    </div>
                    {r.qbi2_s3_link && (
                      <a
                        href={r.qbi2_s3_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-shrink-0 inline-flex items-center gap-1.5 text-teal-700 text-sm font-medium hover:text-teal-800"
                      >
                        Ver PDF
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ------------------------------------------------------------ modal */}
      {modalAbierto && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setModalAbierto(false);
          }}
        >
          <div className="bg-white rounded-2xl max-w-sm w-full p-6">
            <div className="flex items-start justify-between mb-3">
              <h4 className="text-lg font-semibold text-gray-900">Tu documento</h4>
              <button onClick={() => setModalAbierto(false)} aria-label="Cerrar">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4 leading-relaxed">
              Lo usamos solo para encontrar las recetas emitidas a tu nombre. No se
              comparte con nadie.
            </p>
            <input
              type="text"
              inputMode="numeric"
              value={dni}
              onChange={(e) => setDni(e.target.value)}
              placeholder="Sin puntos"
              autoFocus
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
            <button
              onClick={guardarDni}
              disabled={guardando}
              className="mt-4 w-full flex items-center justify-center gap-2 bg-teal-600 text-white py-3 rounded-xl font-semibold disabled:opacity-60"
            >
              {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {guardando ? 'Guardando…' : 'Guardar y buscar mis recetas'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MiCuentaPaciente;
